import React, { memo, useState, useCallback } from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { dropDownStyles } from './styles';

interface LazyDropdownCellProps {
  value: string | null | undefined;
  options: string[];
  itemId: string;
  columnKey: string;
  onSave: (itemId: string, columnKey: string, value: string) => void;
}

export const CustomDropDown = memo<LazyDropdownCellProps>(
  ({ value, options, itemId, columnKey, onSave }) => {
    const [isOpen, setIsOpen] = useState(false);
    const displayColor =
      !value || value === 'Select' ? abyssColors.primary[300] : abyssColors.primary[500];
    const selectValue = value === 'Select' ? '' : (value ?? '');

    const handleOpen = useCallback((e: React.SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleChange = useCallback(
      (e: { target: { value: string } }) => {
        const newValue = e.target.value;
        setIsOpen(false);
        onSave(itemId, columnKey, newValue);
      },
      [itemId, columnKey, onSave]
    );

    return (
      <FormControl
        fullWidth
        size="small"
        variant="standard"
        sx={dropDownStyles(displayColor)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Select
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          value={selectValue}
          displayEmpty
          renderValue={(selected) => (selected as string) || 'Select'}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            border: 'none',
            '&:focus': {
              backgroundColor: 'transparent',
            },
          }}
          MenuProps={{
            disablePortal: false,
            keepMounted: false,
          }}
        >
          <MenuItem value="">N/A</MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.itemId === nextProps.itemId &&
    prevProps.columnKey === nextProps.columnKey &&
    prevProps.options === nextProps.options
);

CustomDropDown.displayName = 'CustomDropDown';
